import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PersonDetailResponse,
  UpdatePersonRequest,
  CreatePersonRequest,
  CreatePersonResponse,
  AssignRoleRequest,
  AssignRoleResponse,
} from '@stfors/shared-types';
import { api, uploadFile } from '../client';

export function usePerson(id: number | null) {
  return useQuery({
    enabled: id !== null && id > 0,
    queryKey: ['person', id],
    queryFn: ({ signal }) => api.get<PersonDetailResponse>(`/api/people/${id ?? 0}`, signal),
  });
}

export function useUpdatePerson(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePersonRequest) =>
      api.patch<PersonDetailResponse>(`/api/people/${id}`, input),
    onSuccess: () => {
      // The person row is shared across roles + the directory, so refresh
      // everything that could surface this name.
      void qc.invalidateQueries({ queryKey: ['person', id] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['contractors'] });
      void qc.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

// Set/replace a person's contact photo (raw image bytes → POST /:id/photo).
export function useSetPersonPhoto(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadFile<void>(`/api/people/${id}/photo`, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', id] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
    },
  });
}

// Remove a person's contact photo (→ DELETE /:id/photo), reverting to initials.
export function useDeletePersonPhoto(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>(`/api/people/${id}/photo`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', id] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
    },
  });
}

// Standalone person create (Add Individual).
export function useCreatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePersonRequest) =>
      api.post<CreatePersonResponse>('/api/people', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['directory'] }),
  });
}

// Attach a role to an existing person. Unbound (the person id is known only
// after the create step), so the id rides in the mutation variables.
export interface AssignPersonRoleInput extends AssignRoleRequest {
  personId: number;
}
export function useAssignPersonRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ personId, ...body }: AssignPersonRoleInput) =>
      api.post<AssignRoleResponse>(`/api/people/${personId}/roles`, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['person', vars.personId] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['contractors'] });
      void qc.invalidateQueries({ queryKey: ['vendors'] });
      // Contact role writes organization_contacts — refresh company detail lists.
      void qc.invalidateQueries({ queryKey: ['organization'] });
      void qc.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

// Link an existing person to a company as a contact (organization_contacts).
// Reuses POST /api/organizations/:id/contacts with an existing person_id.
export interface LinkPersonToOrgInput {
  orgId: number;
  person_id: number;
  title?: string | null;
  is_primary?: boolean;
}
export function useLinkPersonToOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, ...body }: LinkPersonToOrgInput) =>
      api.post<{ contacts: unknown[] }>(`/api/organizations/${orgId}/contacts`, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['organization', vars.orgId] });
      void qc.invalidateQueries({ queryKey: ['person', vars.person_id] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
    },
  });
}
